/**
 * 🧠 AICoachInsightCard — reusable "Lecture Coach YUMIX" card.
 *
 * Iter. 406.fr.43 — Frontend wiring of the shared analytics AI coach
 * (backend Iter. 406.fr.42). Drop this component on any analytics page,
 * point it at the matching /ai-summary endpoint, and it handles :
 *   • lazy fetch (non-blocking for the parent page)
 *   • loading spinner + "Le coach analyse ta courbe…" copy
 *   • graceful empty state (invisible when no data or gen fails)
 *   • gradient glow shell consistent with MacrosInsights
 *
 * Usage:
 *   <AICoachInsightCard endpoint="/hydration/ai-summary" period="week" />
 *   <AICoachInsightCard endpoint="/sleep/ai-summary" period={period} />
 *   <AICoachInsightCard endpoint="/activities/ai-summary" period="week" />
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Sparkles, Loader2 } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';

export default function AICoachInsightCard({ endpoint, period = 'week', className = '' }) {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      setLoading(true);
      setSummary(null);
      try {
        const res = await api.get(`${endpoint}?period=${period}`);
        if (!cancelled) setSummary(res.data);
      } catch {
        if (!cancelled) setSummary({ summary: null, source: 'ai_failed' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchIt();
    return () => { cancelled = true; };
  }, [endpoint, period]);

  // Invisible when the AI has nothing to say + not loading
  if (!loading && (!summary || !summary.summary)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-5 ${className}`}
    >
      <div
        className="p-4 rounded-xl bg-gradient-to-br from-orange-500/8 via-purple-500/6 to-cyan-500/8 ring-1 ring-orange-500/25 relative overflow-hidden"
        style={{ boxShadow: '0 0 18px rgba(249,115,22,0.15), inset 0 0 40px rgba(168,85,247,0.05)' }}
        data-testid={`ai-coach-insight-${endpoint.split('/').filter(Boolean)[0]}`}
      >
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 w-7 h-7 rounded-lg bg-orange-500/15 ring-1 ring-orange-500/30 flex items-center justify-center shrink-0">
            <Brain className="w-3.5 h-3.5 text-orange-300" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              {fr ? 'Lecture Coach YUMIX' : 'Coach YUMIX read'}
            </p>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                {fr ? 'Le coach analyse ta courbe…' : 'The coach is analyzing your curve…'}
              </div>
            ) : (
              <p className="text-sm text-foreground leading-relaxed" data-testid="ai-coach-insight-text">
                {summary.summary}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
